import Dexie, { Table } from 'dexie';
import { saveAs } from 'file-saver'
import { exportDB, importDB } from 'dexie-export-import';
import { now } from 'moment';
import flat from 'flat'
import XLSX from 'xlsx';

/** Dexie DB 
 * @interface db.name.Function()
 * @where name is Collection Name
 * @fwhere function are methods of DB
 */
const dbDexie = new Dexie('myDb');
dbDexie.version(1).stores(
    {
        companies: '++id, name',
        invoices: '++invoiceId, invoiceNo, customerName, billDate',
        ledger: '++id, customerName, date',
        stocks: '++productId, productName '
    }
);
dbDexie.version(1.1).stores(
    {
        purchase: '++id, billNo, partyName, billDate',
    }
);
dbDexie.version(1.2).stores(
    {
        invoices: '++invoiceId,id, invoiceNo, customerName, billDate',
        ledger: '++id, customerName, date, isCustomer',
        expenses: '++id, expenseType , expenseName, date'
    }
);
dbDexie.version(1.3).stores(
    {
        ledger: '++id, customerName, date, isCustomer, billId, billNo',
    }
);

export const UserDB = new Dexie('Users');
UserDB.version(1).stores({
    users: 'userId , userName , [firstName+lastName]'
})

/**
 * Save the file blob for Google Drive Upload
 * @function saveDB
 * @returns file blob
 */
export const exportBackup = async () => {
    try {
        let blob = await exportDB(dbDexie, {
            prettyJson: true,
            // eslint-disable-next-line no-undef
        })
        // saveAs(blob, `db-backup.dbcx`)
        console.log('Backup taken successfully');
        return blob;

    } catch (error) {
        console.error(error);
    }
}

/** Export and Saves A DB Backup File */
export const exportData = async () => {
    try {
        let blob = await exportDB(dbDexie, {
            prettyJson: true,
            progressCallback: (progress) => {
                console.log("Progess: ", progress);
            }
            // eslint-disable-next-line no-undef
        })
        let time = new Date().toISOString().split('T')[0];
        saveAs(blob, `db-backup-${time}.dbcx`)
        console.log('Backup taken successfully');

    } catch (error) {
        console.error(error);
    }
}

/** Imports and Load A DB Backup File
 * @param file - DB File 
 */
export const importData = async (file) => {
    try {
        await dbDexie.delete();
        await importDB(file)
        console.log('Import Successfully');
        return true
    } catch (error) {
        console.error(error);
        return false
    }
}


export const importfromExcel = async (file) => {
    try {
        await dbDexie.delete();
        await dbDexie.open();
        let reader = new FileReader();
        reader.onload = function (e) {
            let data = new Uint8Array(e.target.result);
            let workbook = XLSX.read(data, { type: 'array' });

            let sheet_name_list = workbook.SheetNames;
            sheet_name_list.forEach(async (y) => {
                let worksheet = workbook.Sheets[y];
                let raw_data = XLSX.utils.sheet_to_json(worksheet, { raw: true });
                let data = unflattenData(raw_data);
                // console.log(y , data);
                let table = dbDexie.table(y);
                // console.log(table);
                await table.bulkPut(data).then((res) => {
                    console.log('Import Successfully', res);
                }).catch(error => { console.error(error); });
            });
        }
        reader.readAsArrayBuffer(file);
        return true
    } catch (error) {
        console.error(error);
        return false
    }
}

export const exportToExcel = async (tableName) => {
    try {
        let data = await dbDexie.table(tableName).toArray();
        let wb = XLSX.utils.book_new();
        let ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, tableName);
        let wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
        saveAs(new Blob([wbout], { type: "application/octet-stream" }), `${tableName}-${now()}.xlsx`);
        console.log('Excel Export Successfully');
    } catch (error) { console.error(error); }
}

export const exportToExcelAll = async () => {

    const tables = dbDexie.tables;
    try {
        let wb = XLSX.utils.book_new();
        tables.forEach(async (table) => {
            let data = await flatTable(table);
            let ws = XLSX.utils.json_to_sheet(data);
            XLSX.utils.book_append_sheet(wb, ws, table.name);
        });
        setTimeout(() => {
            let time = new Date().toISOString().split('T')[0];
            XLSX.writeFile(wb, `db-backup-${time}.xlsx`);
            console.log('Excel Export Successfully');

        }, 1000);
    } catch (error) { console.error(error); }
}

/** Delete DB */
export const deleteDatabase = async () => {
    try {
        await dbDexie.delete();
        console.log('DB Deleted');

    } catch (error) {
        console.error(error);
    }
}


export class Database {

    constructor() {
        this.db = dbDexie;
        this.users = UserDB.table('users');
        this.companies = this.db.table('companies');
        this.invoices = this.db.table('invoices');
        this.ledger = this.db.table('ledger');
        this.stocks = this.db.table('stocks');
        this.expenses = this.db.table('expenses');
        this.purchase = this.db.table('purchase');
    }


    /** Get All Companies */
    async getCompanies() {
        return await this.companies.toArray();
    }

    async getCurrentCompany() {
        return await this.companies.orderBy('id').last();
    }

    /** Get All Invoices */
    async getInvoices() {
        return await this.invoices.reverse().toArray();
    }

    /** Get All Ledger */
    async getLedger() {
        return await this.ledger.toArray();
    }

    /** Get All Stocks */
    async getStocks() {
        return await this.stocks.toArray();
    }

    /** Get All Expenses */
    async getExpenses() {
        return await this.expenses.toArray();
    }

    /** Get All Purchases */
    async getPurchases() {
        return await this.purchase.reverse().toArray();
    }

    /** Get All Users */
    async getUsers() {
        return await this.users.toArray();
    }

    /** Clear All Data */
    deleteDatabase() {
        return deleteDatabase();
    }

    /** Export All Data */
    exportData() {
        return exportData();
    }

    /** Import All Data */
    importData(file) {
        return importData(file);
    }

    exportToExcel() {
        const tables = [this.invoices, this.ledger, this.stocks, this.expenses, this.purchase, this.companies];
        const wb = XLSX.utils.book_new();
        console.log('Exporting to Excel');
        tables.forEach(async table => {
            const flatData = await flatTable(table);
            const ws = XLSX.utils.json_to_sheet(flatData);
            XLSX.utils.book_append_sheet(wb, ws, table.name);
        });

        console.log(wb);
        setTimeout(() => {
            XLSX.writeFile(wb, `db-backup-${now()}.xlsx`);
        }, 1000);
    }

    log() {
        // console.log(this._allTables);
        console.log(this.db._dbSchema);
    }

}

/**
 * Flatten the table data to a single array
 * @param {Table} table
*/
async function flatTable(table) {
    const data = await table.toArray();
    let flatData = [];
    data.forEach(row => {
        let flatRow = flat.flatten(row);
        flatData.push(flatRow);
    });
    return flatData;
}

function unflattenData(data) {
    let unflattened = [];
    data.forEach(row => {
        let unflattenedRow = flat.unflatten(row);
        unflattened.push(unflattenedRow);
    });
    return unflattened;
}

// export default db;
export const db = new Database();

// db.log();

// let db = dbDexie
export default db;